"use client"

import { useState } from 'react';
import { Dialog, DialogPanel, Transition } from '@headlessui/react';
import { useTranslations } from 'next-intl';

import { Link } from '@/i18n/routing';
import { BurgerIcon } from '@/component/Icons/BurgerIcon';
import { CrossIcon } from '@/component/Icons/CrossIcon';
import { Logo } from '@/component/Icons/Logo';
import { MobileMenu } from './MobileMenu';
import { LanguageDropdown } from './LanguageDropdown';

export const Header = ({ children, bgClass = '' }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const t = useTranslations();

  return (
    <header className={`header-box relative z-20 ${bgClass}`}>
      <nav
        className={`header-nav container relative z-10 flex sm:min-h-[88px] items-center justify-between max-sm:!py-4 py-[18px] 3xl:mb-3 3xl:rounded-b-10 3xl:!shadow-none ${bgClass}`}
        aria-label="Global"
      >
        <div className="flex items-center gap-x-3 min-w-0 w-full max-sm:pl-1.5">
          <Link aria-label="logo" className="logo" href="/" prefetch={false}>
            <Logo className="max-sm:h-5 max-sm:w-auto" />
          </Link>
          {children}
        </div>
        <div className="flex items-center justify-end gap-x-4 ml-3 min-[1120px]:ml-4 header-btn-box">
          <LanguageDropdown />
          <Link
            href="https://core.guestit.se"
            target="_blank"
            className="max-lg:hidden btn btn-second truncate font-bold"
            id="header-btn"
          >
            {t('menu.fourth')}
          </Link>
        </div>
        <div className="flex ml-4 lg:hidden">
          <button
            type="button"
            className="btn btn-icon btn-second max-sm:btn-sm"
            onClick={() => setMobileMenuOpen(true)}
            data-testid="header-menu-btn"
          >
            <span className="sr-only">Open main menu</span>
            <BurgerIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </nav>

      <Dialog
        as="div"
        className="lg:hidden"
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
      >
        <div className="fixed inset-0 z-20" />
        <Transition
          show={mobileMenuOpen}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
          as="div"
        >
          <DialogPanel className="flex flex-col fixed inset-y-0 right-0 z-20 w-full overflow-y-auto bg-white">
            <div className="flex items-center gap-x-6 !p-4 sm:!px-10 sm:min-h-[88px] sm:!py-[18px] max-sm:justify-between">
              <Link
                data-autofocus
                aria-label="logo"
                className="outline-none"
                href="/"
                prefetch={false}
              >
                <Logo className="max-sm:h-5 max-sm:w-auto max-sm:pl-1.5" />
              </Link>
              <div className="ml-auto flex gap-x-4">
                <LanguageDropdown />
                <button
                  type="button"
                  className="btn btn-icon btn-second max-sm:btn-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="sr-only">Close menu</span>
                  <CrossIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
            </div>
            <MobileMenu closeMenu={() => setMobileMenuOpen(false)} />
          </DialogPanel>
        </Transition>
      </Dialog>
    </header>
  );
};
